import * as THREE from 'three'
import {
  CANONICAL_FACE_POSITIONS,
  CANONICAL_FACE_TRIANGLES,
  CANONICAL_FACE_VERTEX_COUNT,
  EAR_ELLIPSOIDS,
  fitLandmarkDepth,
  HEAD_ELLIPSOID,
  NECK_CYLINDER,
  landmarksToMetric,
  type CameraModel,
  type FaceFrame,
  type LandmarkDepthModel,
  type OccluderSpec,
} from '@ar-suite/core'

/** Depth-only material: writes depth, no colour → hides whatever is behind it while showing the video. */
export function createOccluderMaterial(debug = false): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    colorWrite: debug,
    color: 0x22ccff,
    wireframe: debug,
    transparent: debug,
    opacity: debug ? 0.35 : 1,
    side: THREE.DoubleSide,
    // bias slightly deeper so co-planar wearable surfaces (nose pads, temples on cheeks) win the depth test
    polygonOffset: true,
    polygonOffsetFactor: 1,
    polygonOffsetUnits: 4,
  })
}

export type FaceOccluderMode = 'fitted' | 'canonical'

/**
 * Face occluder = the observed 468-pt landmark mesh lifted to camera space (exact 2-D alignment,
 * depth calibrated to the head matrix — `fitted`), or the rigid canonical mesh × head matrix (`canonical`),
 * + an ellipsoid for the skull (hides temple tips / earring backs behind the head).
 */
export class FaceOccluder {
  readonly group = new THREE.Group()
  readonly faceMesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>
  readonly head: THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>
  readonly ears: [THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>, THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>]
  /** Neck cylinder — hides the back half of necklace chains (part of the head group). */
  readonly neck: THREE.Mesh<THREE.CylinderGeometry, THREE.MeshBasicMaterial>
  private positions: THREE.BufferAttribute
  private metric = new Float32Array(CANONICAL_FACE_VERTEX_COUNT * 3)
  private tmp = new THREE.Matrix4()
  private _debug = false
  private spec: OccluderSpec = { type: 'face+head', depthOffset: 0, scale: 1 }
  /** Last fitted landmark depth model (exposed for debugging / other modules). */
  readonly depthModel: LandmarkDepthModel = { depthRef: 0, zScale: 1 }
  mode: FaceOccluderMode = 'fitted'

  constructor() {
    const geo = new THREE.BufferGeometry()
    this.positions = new THREE.BufferAttribute(new Float32Array(CANONICAL_FACE_VERTEX_COUNT * 3), 3)
    this.positions.setUsage(THREE.DynamicDrawUsage)
    geo.setAttribute('position', this.positions)
    geo.setIndex(new THREE.BufferAttribute(CANONICAL_FACE_TRIANGLES, 1))
    this.faceMesh = new THREE.Mesh(geo, createOccluderMaterial())
    this.faceMesh.frustumCulled = false
    this.faceMesh.name = 'face-occluder'
    this.faceMesh.renderOrder = -100

    this.head = new THREE.Mesh(new THREE.SphereGeometry(1, 32, 24), createOccluderMaterial())
    this.head.name = 'head-occluder'
    this.head.renderOrder = -101
    this.head.frustumCulled = false

    const earGeo = new THREE.SphereGeometry(1, 16, 12)
    this.ears = [new THREE.Mesh(earGeo, this.head.material), new THREE.Mesh(earGeo, this.head.material)]
    for (const e of this.ears) {
      e.renderOrder = -101
      e.frustumCulled = false
      e.matrixAutoUpdate = false
    }
    this.ears[0].name = 'ear-occluder-L'
    this.ears[1].name = 'ear-occluder-R'
    this.neck = new THREE.Mesh(new THREE.CylinderGeometry(1, 1, 1, 24, 1), this.head.material)
    this.neck.name = 'neck-occluder'
    this.neck.renderOrder = -101
    this.neck.frustumCulled = false
    this.neck.matrixAutoUpdate = false

    this.group.name = 'face-occluder-group'
    this.group.add(this.faceMesh, this.head, ...this.ears, this.neck)
  }

  get debug(): boolean {
    return this._debug
  }
  set debug(v: boolean) {
    if (v === this._debug) return
    this._debug = v
    for (const m of [this.faceMesh, this.head]) {
      m.material.dispose()
      m.material = createOccluderMaterial(v)
      if (m === this.head && v) m.material.color.set(0xff8800)
    }
    for (const e of this.ears) e.material = this.head.material
    this.neck.material = this.head.material
  }

  setSpec(spec: OccluderSpec): void {
    this.spec = spec
    this.faceMesh.visible = spec.type === 'face' || spec.type === 'face+head'
    this.head.visible = spec.type === 'head' || spec.type === 'face+head'
    for (const e of this.ears) e.visible = this.head.visible
    this.neck.visible = this.head.visible
    this.group.visible = spec.type !== 'none'
  }

  /** Update from a face frame. Call every tracked frame. */
  update(frame: FaceFrame, cam: CameraModel): void {
    if (this.faceMesh.visible) {
      const n = Math.min(CANONICAL_FACE_VERTEX_COUNT, frame.landmarks.length / 3)
      if (this.mode === 'fitted' && n === CANONICAL_FACE_VERTEX_COUNT) {
        fitLandmarkDepth(frame.landmarks, frame.headMatrix, cam, this.depthModel)
        this.depthModel.depthRef += this.spec.depthOffset
        landmarksToMetric(this.metric, frame.landmarks.subarray(0, n * 3), cam, this.depthModel)
        this.faceMesh.matrixAutoUpdate = true
        this.faceMesh.matrix.identity()
        this.faceMesh.position.set(0, 0, 0)
        this.faceMesh.quaternion.identity()
        this.faceMesh.scale.set(1, 1, 1)
        ;(this.positions.array as Float32Array).set(this.metric)
      } else {
        // rigid canonical mesh placed by the head matrix (pushed back by depthOffset along camera z)
        ;(this.positions.array as Float32Array).set(CANONICAL_FACE_POSITIONS)
        this.tmp.fromArray(frame.headMatrix)
        this.faceMesh.matrixAutoUpdate = false
        this.faceMesh.matrix.copy(this.tmp)
        this.faceMesh.matrix.elements[14] -= this.spec.depthOffset
        this.faceMesh.matrixWorldNeedsUpdate = true
      }
      this.positions.needsUpdate = true
      if (this._debug) this.faceMesh.geometry.computeVertexNormals()
    }
    if (this.head.visible) {
      // head ellipsoid in canonical space → camera space through the head matrix
      this.tmp.fromArray(frame.headMatrix)
      this.head.matrixAutoUpdate = false
      const s = this.spec.scale
      const c = HEAD_ELLIPSOID.center
      const r = HEAD_ELLIPSOID.radii
      const local = new THREE.Matrix4().compose(
        new THREE.Vector3(c[0], c[1], c[2] - this.spec.depthOffset),
        new THREE.Quaternion(),
        new THREE.Vector3(r[0] * s, r[1] * s, r[2] * s),
      )
      this.head.matrix.multiplyMatrices(this.tmp, local)
      this.head.matrixWorldNeedsUpdate = true
      for (const [i, def] of [EAR_ELLIPSOIDS.left, EAR_ELLIPSOIDS.right].entries()) {
        const ear = this.ears[i]!
        local.compose(
          new THREE.Vector3(def.center[0] * s, def.center[1] * s, def.center[2] * s - this.spec.depthOffset),
          new THREE.Quaternion(),
          new THREE.Vector3(def.radii[0] * s, def.radii[1] * s, def.radii[2] * s),
        )
        ear.matrix.multiplyMatrices(this.tmp, local)
        ear.matrixWorldNeedsUpdate = true
      }
      local.compose(
        new THREE.Vector3(NECK_CYLINDER.center[0], NECK_CYLINDER.center[1], NECK_CYLINDER.center[2] - this.spec.depthOffset),
        new THREE.Quaternion(),
        new THREE.Vector3(NECK_CYLINDER.radius * s, NECK_CYLINDER.height, NECK_CYLINDER.radius * s),
      )
      this.neck.matrix.multiplyMatrices(this.tmp, local)
      this.neck.matrixWorldNeedsUpdate = true
    }
  }

  dispose(): void {
    this.faceMesh.geometry.dispose()
    this.faceMesh.material.dispose()
    this.head.geometry.dispose()
    this.head.material.dispose()
    this.ears[0].geometry.dispose()
    this.neck.geometry.dispose()
  }
}
